import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useRef, useState } from "react";

import DescriptiveTooltip from "../DescriptiveTooltip";

interface CopyButtonProps {
  /**
   * Optional render prop for custom rendering
   */
  render?: (props: {
    onClick: (e: React.MouseEvent) => void;
  }) => React.ReactNode;

  /**
   * Whether the button is disabled
   */
  disabled?: boolean;
  /**
   * Text to copy to clipboard
   */
  text: string;

  /**
   * Optional tooltip text (defaults to "Copy to clipboard")
   */
  tooltipText?: string;

  /**
   * Optional callback to execute after successful copy
   */
  onCopySuccess?: () => void;

  /**
   * Optional callback to execute if copy fails
   */
  onCopyError?: (error: unknown) => void;

  /**
   * Duration in ms to show the success state (default: 2000ms)
   */
  successDuration?: number;

  /**
   * Whether the button should only be visible on hover of parent
   * The parent should pass isHovered state if this is true
   */
  visibleOnHover?: boolean;

  /**
   * Current hover state (required if visibleOnHover is true)
   */
  isHovered?: boolean;

  /**
   * Size of the button (default: 'small')
   */
  size?: "small" | "medium" | "large";

  /**
   * Optional className for styling
   */
  className?: string;

  /**
   * tabIndex for the button (default: 0). Pass -1 to exclude from Tab order,
   * e.g. when the button is inside an already-focusable table row.
   */
  tabIndex?: number;
}

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>(({ theme, visible }) => ({
  opacity: visible ? 1 : 0,
  transition: theme.transitions.create("opacity", {
    duration: theme.transitions.duration.shorter,
  }),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

interface CopyButtonComponent extends React.FC<CopyButtonProps> {
  Icon: typeof ContentCopyIcon;
}

const CopyButton: CopyButtonComponent = ({
  render,
  text,
  tooltipText = "Copy to clipboard",
  onCopySuccess,
  onCopyError,
  successDuration = 2000,
  visibleOnHover = false,
  isHovered = false,
  size = "small",
  className,
  disabled = false,
  tabIndex,
}) => {
  const [copied, setCopied] = useState(false);
  const visible = visibleOnHover ? isHovered : true;
  const resetTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(resetTimerRef.current), []);

  const handleCopy = async (e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      onCopySuccess?.();

      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => {
        setCopied(false);
      }, successDuration);
    } catch (error) {
      onCopyError?.(error);
    }
  };

  if (render) {
    return render({ onClick: handleCopy });
  }

  return (
    <DescriptiveTooltip title={copied ? "Copied!" : tooltipText}>
      <StyledIconButton
        onClick={handleCopy}
        size={size}
        className={className}
        visible={visible}
        aria-label={tooltipText}
        disabled={disabled}
        tabIndex={tabIndex}
      >
        {copied ? (
          <CheckIcon
            fontSize="inherit"
            color="success"
            data-testid="CheckIcon"
          />
        ) : (
          <ContentCopyIcon fontSize="inherit" />
        )}
      </StyledIconButton>
    </DescriptiveTooltip>
  );
};

CopyButton.Icon = ContentCopyIcon;

export { CopyButton };
